import { useContext, useMemo } from 'react';

import { getSeasonConfig } from './get-season-config';
import { SeasonalThemeContext } from './seasonal-theme-context';
import { useIsAdminRoute } from './use-is-admin-route';

/** Default scale when an overlay is configured without one (overlay 40% wider than the avatar). */
const DEFAULT_OVERLAY_SCALE = 1.4;
/** Default anchor when an overlay is configured without one (geometric center). */
const DEFAULT_OVERLAY_ANCHOR = Object.freeze({ x: 0.5, y: 0.5 });

export interface SeasonalAvatarOverlayConfig {
	/** Whether the seasonal avatar overlay should render. */
	isEnabled: boolean;
	/** Asset URL for the overlay artwork (when enabled). */
	image: string | undefined;
	/** Width of the overlay as a fraction of the avatar's diameter. */
	scale: number;
	/** Anchor point (0–1) inside the artwork that sits on the avatar's center. */
	anchor: { x: number; y: number };
}

/**
 * Returns the active seasonal avatar overlay configuration.
 *
 * The overlay renders when the seasonal theme is enabled, the active
 * season has an overlay configured, and the user is not on an admin
 * route. Admin pages stay business-focused — see `useIsAdminRoute`.
 *
 * Gracefully handles usage outside of `SeasonalThemeProvider` (returns
 * disabled state) so design-system leaf components can call this
 * unconditionally.
 *
 * Sensible defaults are applied when the season config omits values, so
 * configuring an overlay can be as simple as `{ image: foo }` for
 * symmetric, centered artwork.
 */
export const useSeasonalAvatarOverlay = (): SeasonalAvatarOverlayConfig => {
	const context = useContext(SeasonalThemeContext);
	const isAdminRoute = useIsAdminRoute();
	const seasonConfig = getSeasonConfig();

	const isSeasonalThemeEnabled = context?.isSeasonalThemeEnabled ?? false;
	const isActive = isSeasonalThemeEnabled && !isAdminRoute;
	const overlay = seasonConfig.avatarOverlay;

	return useMemo(
		() => ({
			isEnabled: isActive && Boolean(overlay?.image),
			image: isActive ? overlay?.image : undefined,
			scale: overlay?.scale ?? DEFAULT_OVERLAY_SCALE,
			anchor: overlay?.anchor ?? DEFAULT_OVERLAY_ANCHOR,
		}),
		[isActive, overlay]
	);
};
